import React from "react"
import {formatTextTestIds} from "@next/utils"
import {Grid, Typography} from "@mui/material"
import {TextModel} from "models/Text"
import capitalizeFirstLetter from "../../../../utils/capitalizeFirstLetter"
import {SearchApiRequestTypes} from "../../../../config/constants"
import {
    StyledProductTitle,
    StyledNoResultsFound,
    StyledTextWrapper,
    StyledSecondaryText,
    StyledCapitalizedText,
    StyledShowingResultsForText,
    StyledCount,
    CategoryWrapper,
    CategoryHeading,
} from "./components"
import SeoHeading from "../../../seoHeading"

interface Cashmere25ResultsTitleProps {
    type: SearchApiRequestTypes
    title: string
    relaxedQuery: string
    totalResults: number
    isAutocorrected: boolean
    originalSearchTerm: string | null
    hasSelectedFilters: boolean
    overrideHeading: boolean
    text: TextModel
}

export const Cashmere25ResultsTitle = ({
    type,
    title,
    relaxedQuery,
    totalResults,
    isAutocorrected,
    originalSearchTerm,
    hasSelectedFilters,
    overrideHeading,
    text,
}: Cashmere25ResultsTitleProps) => {
    return (
        <StyledProductTitle data-testid={formatTextTestIds("plp-results-title-wrapper")}>
            <div id="plp-results-title-container" data-default-title={title} data-testid="plp-results-title-container">
                {(!isAutocorrected || hasSelectedFilters) && (
                    <>
                        {type === SearchApiRequestTypes.Keyword && relaxedQuery !== "" && hasSelectedFilters && (
                            <Grid
                                container
                                wrap="nowrap"
                                data-testid={formatTextTestIds("plp-header-wrapper")}
                                justifyContent="center"
                            >
                                <h1
                                    data-testid={formatTextTestIds("plp-results-title")}
                                    title={`"${relaxedQuery}" (${totalResults})`}
                                >
                                    <StyledTextWrapper data-testid="plp-product-title-text">
                                        &quot;
                                        {
                                            capitalizeFirstLetter(
                                                relaxedQuery,
                                            ) /* css capitalization fails on firefox due to quotes */
                                        }
                                        &quot;
                                    </StyledTextWrapper>
                                </h1>
                                <StyledCount
                                    id="plp-results-count-cotton-text"
                                    data-testid="plp-results-count-cotton-text"
                                >
                                    &nbsp;({totalResults})
                                </StyledCount>
                            </Grid>
                        )}
                        {type === SearchApiRequestTypes.Keyword && relaxedQuery !== "" && !hasSelectedFilters && (
                            <>
                                <Grid
                                    container
                                    wrap="nowrap"
                                    data-testid={formatTextTestIds("plp-header-wrapper")}
                                    justifyContent="center"
                                >
                                    <Typography variant="h1" data-testid={formatTextTestIds("plp-results-title")}>
                                        <StyledTextWrapper data-testid="plp-product-title-text">
                                            {text.pages.products.showingResultsNearestMatches}
                                        </StyledTextWrapper>
                                    </Typography>
                                    <StyledCount
                                        id="plp-results-count-cotton-text"
                                        data-testid="plp-results-count-cotton-text"
                                    >
                                        &nbsp;({totalResults})
                                    </StyledCount>
                                </Grid>
                                <StyledNoResultsFound
                                    data-testid={formatTextTestIds("plp-no-results-text")}
                                    $enableCentreTitle
                                >
                                    {text.pages.products.zeroResultsFor} &quot;
                                    {title}
                                    &quot;
                                </StyledNoResultsFound>
                            </>
                        )}
                        {type === SearchApiRequestTypes.Keyword && relaxedQuery === "" && (
                            <Grid
                                container
                                wrap="nowrap"
                                data-testid={formatTextTestIds("plp-header-wrapper")}
                                justifyContent="center"
                            >
                                <h1 data-testid={formatTextTestIds("plp-results-title")}>
                                    <StyledTextWrapper data-testid="plp-product-title-text">
                                        &quot;
                                        {title}
                                        &quot;
                                    </StyledTextWrapper>
                                </h1>
                                <StyledCount
                                    id="plp-results-count-cotton-text"
                                    data-testid="plp-results-count-cotton-text"
                                >
                                    &nbsp;({totalResults})
                                </StyledCount>
                            </Grid>
                        )}
                        {type === SearchApiRequestTypes.Category && (
                            <Grid
                                container
                                wrap="nowrap"
                                data-testid={formatTextTestIds("plp-header-wrapper")}
                                justifyContent="center"
                            >
                                <CategoryWrapper data-testid="plp-product-title">
                                    {overrideHeading ? (
                                        <SeoHeading />
                                    ) : (
                                        <CategoryHeading>
                                            <h1>
                                                <StyledTextWrapper>{title}</StyledTextWrapper>
                                            </h1>
                                            <StyledCount>&nbsp;({totalResults})</StyledCount>
                                        </CategoryHeading>
                                    )}
                                </CategoryWrapper>
                            </Grid>
                        )}
                    </>
                )}

                {isAutocorrected && !hasSelectedFilters && (
                    <>
                        <Grid
                            container
                            wrap="nowrap"
                            data-testid={formatTextTestIds("plp-header-wrapper")}
                            justifyContent="center"
                        >
                            <Typography
                                variant="h1"
                                data-testid={formatTextTestIds("plp-results-title")}
                                title={`${text.pages.products.showingResultsFor} "${title}"`}
                            >
                                <StyledShowingResultsForText>
                                    {text.pages.products.showingResultsFor}&nbsp;
                                </StyledShowingResultsForText>
                                <StyledCapitalizedText>
                                    &quot;
                                    {title}
                                    &quot;
                                </StyledCapitalizedText>
                            </Typography>
                            <StyledCount id="plp-results-count-cotton-text" data-testid="plp-results-count-cotton-text">
                                &nbsp;({totalResults})
                            </StyledCount>
                        </Grid>
                        <StyledSecondaryText data-testid={formatTextTestIds("plp-no-results-text")} $enableCentreTitle>
                            {text.pages.products.zeroResultsFor} &quot;
                            {originalSearchTerm}
                            &quot;
                        </StyledSecondaryText>
                    </>
                )}
            </div>
        </StyledProductTitle>
    )
}

export default Cashmere25ResultsTitle
